import React, { useState, useContext } from 'react';
import { FormControlLabel, Grid, Switch, Typography, withStyles } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import InfoIcon from '@material-ui/icons/Info';
import cn from 'classnames';
import { useDispatch } from 'react-redux';
import { compose } from 'recompose';
import {
  UI_MODIFYING_INFO_MODAL_CANCEL,
  UI_MODIFYING_INFO_MODAL_CLOSE,
  UI_MODIFYING_INFO_MODAL_CONTINUE,
  UI_MODIFYING_INFO_MODAL_SWITCH,
} from '../../../State/Store/UI/UI.types';
import themeCreateInjectableStyles from '../../../Support/Theme/CreateInjectableStyles/ThemeCreateInjectableStyles.support';
import Modal from '../../Modal/Modal.component';
import ModifyingInfoModalStyles from '../EditOrder.component.styles';
import hitGTM from 'State/Store/UI/Actions/GTM.action';
import ServicesContext from 'Contexts/Services/Services.context';
import { ORDER_EDIT_INFO_ALREADY_CONFIRMED_ON_THIS_DEVICE } from 'Constants/Order/Order.constants';
import envGet from '../../../Support/Env/Get/EnvGet.support';

const enableModifyingDate = envGet({ key: `REACT_APP_BORAL_FEATURE_TOGGLE_ENABLE_MODIFYING_DATE` }) === `true`;

interface Props {
  open: boolean;
  shouldHideSwitch?: boolean;
  showOkBtnOnly?: boolean;
  onClose(): void;
  onContinue(): void;
  classes: any;
  content?: any;
  order?: any;
  width?: any;
  title?: any;
  gtmTagContinue?: any;
  gtmTagClose?: any;
  gtmTagCancel?: any;
  gtmTagSwitch?: any;
  localStorageFlag?: any;
}

const ModifyingInfoModalComp = (props: Props) => {
  const {
    open,
    shouldHideSwitch = false,
    showOkBtnOnly = false,
    onClose,
    onContinue,
    classes,
    content,
    title,
    gtmTagSwitch,
    gtmTagContinue,
    gtmTagClose,
    gtmTagCancel,
    localStorageFlag,
    width,
    order,
  } = props;
  const dispatch = useDispatch();
  const services = useContext(ServicesContext);
  const [isSwitchOn, setIsSwitchOn] = useState(false);

  const onCloseHandler = () => {
    dispatch(hitGTM(gtmTagClose || UI_MODIFYING_INFO_MODAL_CLOSE));
    onClose();
  };

  const onCancelHandler = () => {
    dispatch(hitGTM(gtmTagCancel || UI_MODIFYING_INFO_MODAL_CANCEL));
    onClose();
  };

  const onContinueHandler = () => {
    dispatch(hitGTM(gtmTagContinue || UI_MODIFYING_INFO_MODAL_CONTINUE));
    if (isSwitchOn) {
      services.storageService.setItem(localStorageFlag || ORDER_EDIT_INFO_ALREADY_CONFIRMED_ON_THIS_DEVICE, true);
    }
    onContinue();
  };

  const onSwitchHandler = (isOn: any) => {
    dispatch(hitGTM(gtmTagSwitch || UI_MODIFYING_INFO_MODAL_SWITCH));
    const checked = isOn.currentTarget.checked;
    setIsSwitchOn(checked);
  };

  return (
    <Modal
      footer={
        !showOkBtnOnly ? (
          <Grid className={classes.textAlignRight} container justify="flex-end" spacing={2}>
            <Grid className={classes.flexGrow1} item>
              <Button className={classes.button} color="primary" onClick={onCancelHandler} variant="outlined">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button className={classes.button} color="primary" onClick={onContinueHandler} variant="contained">
                Continue{' '}
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Grid className={classes.textAlignRight} container justify="flex-end" spacing={2}>
            <Grid className={classes.flexGrow1} item>
              <Button className={classes.button} color="primary" onClick={onCloseHandler} variant="contained">
                OK
              </Button>
            </Grid>
          </Grid>
        )
      }
      maxWidth="xs"
      icon={<InfoIcon />}
      content={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {content || (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="body2">At this time you can modify the following order fields</Typography>
                </Grid>
                <Grid item xs={12}>
                  <ul className={classes.infoList}>
                    <li>- {enableModifyingDate ? `Delivery Date & Start Time` : `Start Time`}</li>
                    <li>- Quantity</li>
                    <li>- Driver Instructions</li>
                    <li>- Site Contact Information</li>
                  </ul>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    To discuss other changes please{' '}
                    <a
                      className={cn(classes.textDecorationUnderline, classes.cursorPointer, classes.hyperlink, classes.callBoral)}
                      rel="noopener noreferrer"
                      target="_blank"
                      href="https://www.boral.com.au/locations?product_category%5B86%5D=86"
                    >
                      Call Boral{!isWidthDown('sm', width) && order && order.details && order.details.boralTel ? ` (${order.details.boralTel})` : ''}
                    </a>
                    .
                  </Typography>
                </Grid>
              </Grid>
            )}
            {!shouldHideSwitch && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch color="primary" checked={isSwitchOn} onChange={onSwitchHandler} />}
                  label={<Typography variant="body2">{`Don't show this again`}</Typography>}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      }
      open={open}
      onClose={onCloseHandler}
      title={
        <Grid container>
          <Grid item xs={12}>
            {title || `Modifying an order`}
          </Grid>
        </Grid>
      }
      paperClasses={classes.paperClassesInfo}
    />
  );
};

const ModifyingInfoModal = compose<any, any>(
  withWidth(),
  withStyles(themeCreateInjectableStyles(ModifyingInfoModalStyles) as any, { withTheme: true }),
)(ModifyingInfoModalComp);

export default ModifyingInfoModal;
